import mixpanel from "mixpanel-browser";

import {
  DiscountApplicationType,
  ExperienceListDto,
  LoginResponseBodyUserType,
  ProgressBarConfigType,
  WidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { DashboardPageName } from "@src/api/types/dashboards";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";

export const mpLogin = (user: LoginResponseBodyUserType) => {
  mixpanel.identify(user.user.email);
  mixpanel.register({
    organization: user.organization.name,
    current_organization_id: user.user.organizationId,
    $name: `${user.user.firstName} ${user.user.lastName}`,
    $email: user.user.email,
    ai_enabled: user.organization.configurations.some(
      (config) =>
        config.key === "enable_ai" &&
        (config.value === true || config.value === "true")
    ),
  });
  mixpanel.track("login");
  mixpanel.people.set_once({
    first_login: new Date().toISOString(),
  });
  mixpanel.people.set({
    last_login: new Date().toISOString(),
    $name: `${user.user.firstName} ${user.user.lastName}`,
    $email: user.user.email,
    organization: user.organization.name,
  });
  mixpanel.people.increment("total_login");
};

export const mpLogout = function () {
  mixpanel.track("logout");
  mixpanel.reset();
};

export const mpCompletedWelcome = function (email: string) {
  mixpanel.identify(email);
  mixpanel.track("completed_welcome");
  mixpanel.people.set_once({
    welcome_completed: true,
  });
};

export const mpClickAnalyticsButton = function (
  experimentId: string,
  experimentName: string,
  entryPoint: string
) {
  mixpanel.track("click_analytics_button", {
    experiment_id: experimentId,
    experiment_name: experimentName,
    entry_point: entryPoint,
  });
};

export const mpClickAnalyticsFilterSaveButton = function (
  experienceId: string,
  appliedFilters: Partial<IExperienceDashboardFilters>,
  pageName: DashboardPageName
) {
  mixpanel.track("click_analytics_filter_save", {
    experience_id: experienceId,
    filters: appliedFilters,
    page_name: pageName,
  });
};

export const mpClickSubpageEntryPoint = function (
  experienceId: string,
  entryPoint: string,
  subpage: string,
  metric?: string
) {
  mixpanel.track("click_subpage_entry_point", {
    experience_id: experienceId,
    entry_point: entryPoint,
    subpage: subpage,
    metric: metric,
  });
};

export const mpAnalyticsMetricDetailClick = function (
  experienceId: string,
  entryPoint: string,
  metric: string
) {
  mixpanel.track("click_analytics_metric_detail", {
    experience_id: experienceId,
    entry_point: entryPoint,
    metric: metric,
  });
};

export const mpClickLearnMore = function (
  experienceId: string,
  entryPoint: string,
  link: string
) {
  mixpanel.track("click_learn_more", {
    experience_id: experienceId,
    entry_point: entryPoint,
    link,
  });
};

export const mpClickAddMetric = function (
  experienceId?: string,
  entryPoint?: string,
  metric?: string,
  startTs?: number
) {
  mixpanel.track("click_add_metric", {
    experience_id: experienceId,
    metric: metric,
    start_ts: startTs,
    entry_point: entryPoint,
  });
};

export const mpClickAnalyticsInteractiveLegend = function (
  experienceId: string,
  variationId: string,
  entryPoint: string
) {
  mixpanel.track("click_analytics_interactive_legend", {
    experience_id: experienceId,
    variation_id: variationId,
    entry_point: entryPoint,
  });
};

export const mpViewPage = function (pageName: string) {
  mixpanel.track("view_page", {
    page_name: pageName,
  });
};

export const mpClickCreateExperienceButton = function () {
  mixpanel.track("click_create_experiment_button");
};

export const mpClickCreatePersonalizationButton = function () {
  mixpanel.track("click_create_personalization_button");
};

export const mpClickAiScreenshotRecommendationsInspireMeButton = function () {
  mixpanel.track("click_ai_screenshot_recommendations_inspire_me_button");
};

export const mpClickAiScreenshotRecommendationsRegenerateButton = function () {
  mixpanel.track("click_ai_screenshot_recommendations_regenerate_button");
};

export const mpClickAiScreenshotRecommendationsCreateTestButton = function () {
  mixpanel.track("click_ai_screenshot_recommendations_create_test_button");
};

export const mpClickAiExperienceAnalysisButton = function () {
  mixpanel.track("click_ai_experiment_analysis_button");
};

export const mpClickAiExperienceAnalysisSummaryButton = function () {
  mixpanel.track("click_ai_experiment_analysis_summary_button");
};

export const mpClickCompareGroupSwapButton = function () {
  mixpanel.track("click_compare_group_swap_button");
};

export const mpClickAiExperienceAnalysisTestNextButton = function () {
  mixpanel.track("click_ai_experiment_analysis_test_next_button");
};

export const mpClickAiExperienceAnalysisCustomButton = function () {
  mixpanel.track("click_ai_experiment_analysis_custom_button");
};

export const mpClickAiExperienceAskAbieButton = function (query: string) {
  mixpanel.track("click_ai_experiment_ask_abie_button", { query });
};

export const mpClickAiExperienceRegenerateButton = function () {
  mixpanel.track("click_ai_experiment_regenerate_button");
};

export const mpClickEditExperienceButton = function () {
  mixpanel.track("click_edit_experiment_button");
};

export const mpClickExperienceTableName = function (
  location: "analytics" | "edit"
) {
  mixpanel.track("click_experiment_table_name_button", { sentTo: location });
};

export const mpClickPersonalizationOverviewName = function (
  location: "analytics" | "edit"
) {
  mixpanel.track("click_personalization_overview_name_button", {
    sentTo: location,
  });
};
export const mpClickDuplicateExperienceButton = function () {
  mixpanel.track("click_duplicate_experiment_button");
};

export const mpClickHeaderNavigationButton = function (
  button: "analytics" | "preview" | "details",
  experimentId: string
) {
  mixpanel.track("click_duplicate_experiment_button", {
    experiment_id: experimentId,
    sentTo: button,
  });
};

export const mpRateAi = (
  feature: string,
  rating: string,
  sessionId: string
) => {
  mixpanel.track(`${feature}_rating`, {
    rating,
    sessionId,
  });
};

export const unixToISO = function (time: number) {
  return new Date(time * 1000).toISOString();
};

export const mpClickSaveExperienceButton = function (
  experimentId: string | undefined,
  experimentName: string,
  experimentType: string,
  experimentCreateTime: number | null | undefined,
  performingUserId: string | null
) {
  mixpanel.track("click_save_experiment_button", {
    experiment_id: experimentId,
    experiment_name: experimentName,
    experiment_type: experimentType,
    date_created: experimentCreateTime
      ? unixToISO(experimentCreateTime)
      : new Date().toISOString(),
    date_modified: experimentCreateTime ? new Date().toISOString() : null,
    performing_user: performingUserId,
  });
};

export const mpClickSavePersonalizationButton = function (
  id: string | undefined,
  name: string,
  createTime: number | null | undefined,
  performingUserId: string | null
) {
  mixpanel.track("click_save_personalization_button", {
    personalization_id: id,
    personalization_name: name,
    date_created: createTime ? unixToISO(createTime) : new Date().toISOString(),
    date_modified: createTime ? new Date().toISOString() : null,
    performing_user: performingUserId,
  });
};

export const mpStartExperience = function (experiment: {
  id: string;
  name: string;
  pausedAtTs: number | null;
}) {
  // want to maintain original date_start
  if (experiment.pausedAtTs) {
    mixpanel.track("start_experiment", {
      experiment_id: experiment.id,
      experiment_name: experiment.name,
      date_paused: new Date().toISOString(),
    });
  } else {
    mixpanel.track("start_experiment", {
      experiment_id: experiment.id,
      experiment_name: experiment.name,
      date_started: new Date().toISOString(),
    });
  }
};

export const mpEndExperience = function (experiment: {
  id: string;
  name: string;
}) {
  mixpanel.track("end_experiment", {
    experiment_id: experiment.id,
    experiment_name: experiment.name,
    date_ended: new Date().toISOString(),
  });
};

export const mpPauseExperience = function (experiment: {
  name: string;
  id: string;
}) {
  mixpanel.track("pause_experiment", {
    experiment_id: experiment.id,
    experiment_name: experiment.name,
    date_paused: new Date().toISOString(),
  });
};

export const mpClickEditProgressBarButton = function (entryPoint: string) {
  mixpanel.track("click_edit_progress_bar_button", {
    entry_point: entryPoint,
  });
};

export const mpClickSaveProgressBarButton = function (
  progressBarConfig: ProgressBarConfigType
) {
  mixpanel.track("click_save_progress_bar_button", {
    progress_bar_config: progressBarConfig,
  });
};

export const mpClickDocLink = function (docLink: string) {
  mixpanel.track("click_doc_link", {
    doc_link: docLink,
  });
};

export const mpClickButton = function (buttonId: string) {
  mixpanel.track("click_button", {
    button_id: buttonId,
  });
};

export const mpClickDeleteExperience = function (
  experiment: Omit<Pick<ExperienceListDto, "testTypes" | "name">, "id"> & {
    id?: string;
  },
  performingUserId: string | null
) {
  mixpanel.track("delete_experiment", {
    experiment_id: experiment.id,
    experiment_name: experiment.name,
    performing_user: performingUserId,
  });
};

export const mpClickToggleCampaign = function (
  campaign: Pick<ExperienceListDto, "id" | "name">,
  performingUserId: string | null,
  toggle: boolean
) {
  mixpanel.track("click_toggle_campaign", {
    performing_user: performingUserId,
    toggle: toggle,
    campaign_name: campaign.name,
    campaign_id: campaign.id,
  });
};

export const mpClickCreateCampaignButton = function () {
  mixpanel.track("click_create_campaign_button");
};
export const mpClickEditCampaignButton = function () {
  mixpanel.track("click_edit_campaign_button");
};

export const mpClickEditWidgetButton = function (widgetType: WidgetType) {
  mixpanel.track("click_edit_widget_button", {
    widget_type: widgetType,
  });
};

export const mpClickDuplicateCampaignButton = function () {
  mixpanel.track("click_duplicate_campaign_button");
};

export const mpClickSaveCampaignButton = function (
  campaign: ExperienceFe,
  discountType: DiscountApplicationType,
  campaignCreateTime: number | null | undefined,
  performingUserId: string | null
) {
  mixpanel.track("click_save_campaign_button", {
    campaign_id: campaign.id,
    campaign_name: campaign.name,
    discount_type: discountType,
    date_created: campaignCreateTime
      ? unixToISO(campaignCreateTime)
      : new Date().toISOString(),
    date_modified: campaignCreateTime ? new Date().toISOString() : null,
    performing_user: performingUserId,
    requires_link_access: campaign.requiresLink,
  });
};

export const mpClickSaveWidgetButton = function (
  performingUserId: string | null,
  widget: WidgetDto
) {
  mixpanel.track("click_save_widget_button", {
    widget_id: widget.id,
    widget_type: widget.widgetType,
    performing_user: performingUserId,
  });
};

export const mpClickDeleteCampaign = function (
  campaign: Omit<Pick<ExperienceListDto, "testTypes" | "name">, "id"> & {
    id?: string;
  },
  performingUserId: string | null
) {
  mixpanel.track("delete_campaign", {
    campaign_id: campaign.id,
    campaign_name: campaign.name,
    performing_user: performingUserId,
  });
};

export const mpTrackBillingButtonClick = function (
  planName: string | undefined,
  planId: string,
  performingUserId: string | null
) {
  mixpanel.track("click_approve_billing", {
    planName,
    planId,
    performing_user: performingUserId,
  });
};

export const mpClickShareToCommunity = function (experienceId: string) {
  mixpanel.track("click_share_to_community", {
    experience_id: experienceId,
  });
};

export const mpClickShareToX = function (experienceId: string) {
  mixpanel.track("click_share_to_x", {
    experience_id: experienceId,
  });
};

export const mpClickShare = function (experienceId: string | undefined) {
  mixpanel.track("click_share", {
    experience_id: experienceId,
  });
};

export const mpOnboardingStepComplete = function (
  stepName: string,
  currentStep: number,
  data: { [key: string]: string | null | undefined | string[] }
) {
  mixpanel.track("onboarding_step_complete", {
    screen_name: stepName,
    screen_order: currentStep + 1, // add 1 bc of index
    data,
  });
};

export const mpScriptIntegration = function (
  screenName: string,
  buttonClicked: string
) {
  mixpanel.track("script_integration", {
    button_clicked: buttonClicked,
    screen_name: screenName,
  });
};

export const mpClickOffersMarketingBanner = function () {
  mixpanel.track("click_offers_announcement_banner");
};
